import { EntryType } from "@atoms/atom-types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface DebugProtocol {
  id: string;
  timestamp: number;
  operationName: string;
  duration: number;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  entries: any[];
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  context?: Record<string, any>;
  error?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  raw?: any; // Store the raw protocol data
  transactionId?: string;
  parentTransactionId?: string;
  isFork?: boolean;
  info?: string;
}

interface DebugProtocolStore {
  protocols: DebugProtocol[];
  maxProtocols: number;
  addProtocol: (protocol: DebugProtocol) => void;
  clearProtocols: () => void;
  removeProtocol: (id: string) => void;
  setMaxProtocols: (max: number) => void;
}

// Custom storage that uses IndexedDB via localStorage fallback
const storage = {
  getItem: async (name: string): Promise<string | null> => {
    try {
      // Try IndexedDB first
      if (typeof window !== "undefined" && "indexedDB" in window) {
        const db = await openDB();
        const transaction = db.transaction(["debug-protocols"], "readonly");
        const store = transaction.objectStore("debug-protocols");
        const result = await promisifyRequest(store.get(name));
        return result ? JSON.stringify(result) : null;
      }
    } catch (error) {
      console.warn(
        "IndexedDB not available, falling back to localStorage",
        error,
      );
    }
    // Fallback to localStorage
    return localStorage.getItem(name);
  },
  setItem: async (name: string, value: string): Promise<void> => {
    try {
      // Try IndexedDB first
      if (typeof window !== "undefined" && "indexedDB" in window) {
        const db = await openDB();
        const transaction = db.transaction(["debug-protocols"], "readwrite");
        const store = transaction.objectStore("debug-protocols");
        await promisifyRequest(store.put(JSON.parse(value), name));
        return;
      }
    } catch (error) {
      console.warn(
        "IndexedDB not available, falling back to localStorage",
        error,
      );
    }
    // Fallback to localStorage
    localStorage.setItem(name, value);
  },
  removeItem: async (name: string): Promise<void> => {
    try {
      // Try IndexedDB first
      if (typeof window !== "undefined" && "indexedDB" in window) {
        const db = await openDB();
        const transaction = db.transaction(["debug-protocols"], "readwrite");
        const store = transaction.objectStore("debug-protocols");
        await promisifyRequest(store.delete(name));
        return;
      }
    } catch (error) {
      console.warn(
        "IndexedDB not available, falling back to localStorage",
        error,
      );
    }
    // Fallback to localStorage
    localStorage.removeItem(name);
  },
};

// Helper to promisify IndexedDB requests
function promisifyRequest<T = undefined>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Open or create IndexedDB
let dbPromise: Promise<IDBDatabase> | null = null;
function openDB(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open("debug-store", 1);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("debug-protocols")) {
          db.createObjectStore("debug-protocols");
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return dbPromise;
}

export const useDebugProtocolStore = create<DebugProtocolStore>()(
  persist(
    (set) => ({
      protocols: [],
      maxProtocols: 100, // Default max protocols to store

      addProtocol: (protocol) =>
        set((state) => {
          // Log incoming protocol for debugging
          console.log("Raw protocol data received:", protocol);

          // Handle the protocol data structure
          let operationName = "Unknown";
          let duration = 0;
          // biome-ignore lint/suspicious/noExplicitAny: legacy code
          let entries: any[] = [];

          // The protocol from subscription is { user, protocol }
          // biome-ignore lint/suspicious/noExplicitAny: legacy code
          const protocolData = (protocol as any).protocol || protocol;

          // Check if protocol is an array (ProtocolEntry[])
          if (Array.isArray(protocolData)) {
            entries = protocolData;
            // Calculate duration from entries
            if (entries.length > 0) {
              // biome-ignore lint/suspicious/noExplicitAny: legacy code
              const times = entries.map((e: any) => e.time);
              duration = Math.max(...times) - Math.min(...times);
            }
            // Try to extract operation name from first entry's extraInfo
            if (entries.length > 0 && entries[0].extraInfo) {
              operationName = entries[0].extraInfo;
            } else {
              // Fallback: try to find any entry with operation info
              const operationEntry = entries.find(
                // biome-ignore lint/suspicious/noExplicitAny: legacy code
                (e: any) =>
                  e.extraInfo &&
                  (e.extraInfo.includes("operation:") ||
                    e.extraInfo.includes("reducer:") ||
                    e.extraInfo.startsWith("[")),
              );
              if (operationEntry?.extraInfo) {
                if (operationEntry.extraInfo.includes("operation:")) {
                  operationName = operationEntry.extraInfo
                    .replace("operation:", "")
                    .trim();
                } else if (operationEntry.extraInfo.includes("reducer:")) {
                  operationName = operationEntry.extraInfo
                    .replace("reducer:", "")
                    .trim();
                } else {
                  operationName = operationEntry.extraInfo;
                }
              }
            }
          } else if (protocolData.entries) {
            // Protocol has entries property
            entries = protocolData.entries;
            operationName = protocolData.operationName || "Unknown";
            duration = protocolData.duration || 0;
          }

          // Check for errors in debug entries if error field is empty
          let errorMessage = protocol.error || "";
          if (!errorMessage && entries.length > 0) {
            // Look for error debug infos in entries
            // Only consider it an error if:
            // 1. It has errorType or errorCode (actual error indicators)
            // 2. It has a stack trace that starts with an Error
            // 3. The extraInfo indicates it's an error (empty extraInfo with error debugInfos)
            // biome-ignore lint/suspicious/noExplicitAny: legacy code
            const errorEntry = entries.find((e: any) => {
              if (e.type !== EntryType.DebugInfo || !e.debugInfos) return false;

              // Check if it's actually an error entry
              const hasErrorType =
                e.debugInfos.errorType &&
                e.debugInfos.errorType !== "Permission";
              const hasErrorCode = typeof e.debugInfos.errorCode === "string";
              const hasErrorStack = e.debugInfos.stack?.includes("Error");

              // If extraInfo is empty and we have error indicators, it's likely an error
              const isLikelyError =
                (e.extraInfo === "" ||
                  e.extraInfo === null ||
                  e.extraInfo === undefined) &&
                (hasErrorType || hasErrorCode || hasErrorStack);

              return hasErrorType || hasErrorCode || isLikelyError;
            });

            if (errorEntry) {
              // Extract error message from debug infos
              if (errorEntry.extraInfo?.trim()) {
                errorMessage = errorEntry.extraInfo;
              } else if (errorEntry.debugInfos.stack) {
                // Parse error from stack trace
                const stackLines = errorEntry.debugInfos.stack.split("\n");
                const firstLine = stackLines[0] || "";
                // Extract error message from first line of stack (e.g., "Error: message" or just "Error: ")
                const match = firstLine.match(/^(\w+Error|Error):\s*(.*)$/);
                if (match) {
                  const errorType = match[1];
                  const message = match[2]?.trim() || "";
                  errorMessage = message
                    ? `${errorType}: ${message}`
                    : `${errorType} at ${operationName}`;
                } else {
                  errorMessage =
                    errorEntry.debugInfos.errorType || "Error occurred";
                }
              } else if (errorEntry.debugInfos.errorType) {
                errorMessage = `${errorEntry.debugInfos.errorType}: ${errorEntry.debugInfos.message || "Unknown error"}`;
              } else {
                errorMessage = "Error occurred (check protocol details)";
              }
            }
          }

          const newProtocol: DebugProtocol = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: Date.now(),
            operationName:
              protocol.isFork && protocol.info
                ? `Fork: ${protocol.info}`
                : operationName,
            duration,
            entries,
            context: protocol.context,
            error: errorMessage,
            raw: protocol,
            transactionId: protocol.transactionId,
            parentTransactionId: protocol.parentTransactionId,
            isFork: protocol.isFork,
            info: protocol.info,
          };

          const newProtocols = [newProtocol, ...state.protocols];

          // Keep only the latest maxProtocols
          if (newProtocols.length > state.maxProtocols) {
            newProtocols.splice(state.maxProtocols);
          }

          return { protocols: newProtocols };
        }),

      clearProtocols: () => set(() => ({ protocols: [] })),

      removeProtocol: (id) =>
        set((state) => ({
          protocols: state.protocols.filter((p) => p.id !== id),
        })),

      setMaxProtocols: (max) =>
        set((state) => {
          const newProtocols = state.protocols.slice(0, max);
          return { maxProtocols: max, protocols: newProtocols };
        }),
    }),
    {
      name: "debug-protocol-storage",
      storage: createJSONStorage(() => storage),
      partialize: (state) => ({
        protocols: state.protocols,
        maxProtocols: state.maxProtocols,
      }),
    },
  ),
);
